import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, FileText, Briefcase, Users, CheckCircle2, XCircle, Inbox, Coins, Sparkles, Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import api from "@/lib/api";

// Kind → icon + chip + route resolver
const KIND_MAP = {
  cv_request_received:    { icon: Inbox,        chip: "tb-chip-sky",      route: () => "/cv-requests" },
  cv_request_approved:    { icon: CheckCircle2, chip: "tb-chip-mint",     route: () => "/cv-requests" },
  cv_request_rejected:    { icon: XCircle,      chip: "tb-chip-rose",     route: () => "/cv-requests" },
  application_applied:    { icon: Briefcase,    chip: "tb-chip-lavender", route: (n) => `/hiring/jobs` },
  application_shortlisted:{ icon: Sparkles,     chip: "tb-chip-butter",   route: () => "/jobs" },
  application_interview:  { icon: Sparkles,     chip: "tb-chip-lavender", route: () => "/jobs" },
  application_selected:   { icon: CheckCircle2, chip: "tb-chip-mint",     route: () => "/jobs" },
  application_rejected:   { icon: XCircle,      chip: "tb-chip-rose",     route: () => "/jobs" },
  application_withdrawn:  { icon: XCircle,      chip: "tb-chip-sky",      route: () => "/jobs" },
  hiring_team_invite:     { icon: Users,        chip: "tb-chip-lavender", route: () => "/hiring/team" },
  coins_credit:           { icon: Coins,        chip: "tb-chip-butter",   route: () => "/wallet" },
  coins_low:              { icon: Coins,        chip: "tb-chip-rose",     route: () => "/wallet" },
  referral_completed:     { icon: Sparkles,     chip: "tb-chip-mint",     route: () => "/wallet" },
};

function resolveKind(n) {
  return KIND_MAP[n.kind] || { icon: Bell, chip: "tb-chip-lavender", route: () => n.href || "/" };
}

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const tickRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/push/notifications");
      setItems(data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    tickRef.current = setInterval(load, 30000);
    return () => clearInterval(tickRef.current);
  }, []);

  const unread = items.filter((i) => !i.read).length;

  const markAll = async () => {
    try {
      await api.post("/push/notifications/read-all");
      load();
    } catch { /* ignore */ }
  };

  const handleOpen = async (n) => {
    try {
      await api.post(`/push/notifications/${n.notification_id}/read`);
    } catch { /* ignore */ }
    setOpen(false);
    const route = n.href || resolveKind(n).route(n);
    if (route) navigate(route);
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="notifications-trigger" aria-label="Notifications" className="relative">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-violet-600 text-white text-[10px] font-semibold grid place-items-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="font-display font-medium text-sm">Notifications</p>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-violet-600 hover:underline" data-testid="mark-all-read">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-auto">
          {items.length === 0 && (
            <div className="px-4 py-10 text-center">
              <Mail className="h-6 w-6 text-muted-foreground inline-block mb-2" />
              <p className="text-sm text-muted-foreground">You&apos;re all caught up ✨</p>
            </div>
          )}
          {items.map((n) => {
            const { icon: Icon, chip } = resolveKind(n);
            return (
              <button
                key={n.notification_id}
                onClick={() => handleOpen(n)}
                data-testid={`notification-${n.notification_id}`}
                className={`block w-full text-left px-3 py-3 border-b border-border hover:bg-muted/50 transition ${n.read ? "" : "bg-violet-50/40 dark:bg-violet-500/5"}`}
              >
                <div className="flex items-start gap-2.5">
                  <span className={`h-8 w-8 rounded-lg ${chip} grid place-items-center shrink-0`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{n.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {new Date(n.created_at).toLocaleString()}
                    </p>
                  </div>
                  {!n.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-violet-500 shrink-0" />}
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
